import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { IoAdd, IoArrowBack } from "react-icons/io5";
import PremiumFolderCard from "./components/PremiumFolderCard";
import CreateFolderModal from "components/modal/CreateFolderModal";
import EditFolderModal from "components/modal/EditFolderModal";
import { getFolders, toggleFavoriteFolder, deleteFolder } from "./mvc/foldersController";

const FoldersContent = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Determine folder type from current location
  const isClientsView = location.pathname.includes("/clients");
  const folderType = isClientsView ? "Client" : "Lead";
  const typeLabel = isClientsView ? "Client" : "Lead";

  // State
  const [allFolders, setAllFolders] = useState([]);
  const [favoriteFolders, setFavoriteFolders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Show toast notification
  const showToast = (message, toastType) => {
    setToast({ show: true, message, type: toastType });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "" });
    }, 3000);
  };

  // Fetch folders
  const fetchFolders = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getFolders(folderType, page, limit);
      setAllFolders(response.allFolders?.data || []);
      setFavoriteFolders(response.favoriteFolders?.data || []);
      setTotal(response.allFolders?.total || 0);
      setTotalPages(Math.max(1, Math.ceil((response.allFolders?.total || 0) / limit)));
      if (!response.success && response.message) {
        setError(response.message);
      }
    } catch (err) {
      const errorMsg = err?.response?.data?.message || err?.message || "Failed to fetch folders";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [folderType, page, limit]);

  useEffect(() => {
    setPage(1); // Reset to first page when path changes
  }, [location.pathname, folderType]);

  useEffect(() => {
    fetchFolders();
  }, [page, limit, folderType, fetchFolders]);
  // Handle favorite toggle
  const handleFavoriteToggle = async (folderId, currentStatus) => {
    try {
      await toggleFavoriteFolder(folderId, currentStatus);
      showToast(currentStatus ? "Removed from favorites" : "Added to favorites", "success");
      setPage(1);
      fetchFolders();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Failed to update favorite status";
      showToast(errorMsg, "error");
    }
  };

  // Handle delete
  const handleDelete = async (folderId) => {
    try {
      await deleteFolder(folderId);
      showToast("Folder deleted successfully", "success");

      if (allFolders.length === 1 && page > 1) {
        setPage((prev) => prev - 1);
        return;
      }

      fetchFolders();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Failed to delete folder";
      showToast(errorMsg, "error");
    }
  };

  // Handle edit
  const handleEdit = (folder) => {
    setSelectedFolder(folder);
    setIsEditModalOpen(true);
  };

  // Handle edit submit
  const handleEditSubmit = async (folderId, updatedFolderData) => {
    try {
      // Update the folder in the list
      setAllFolders((prevState) =>
        prevState.map((f) => (f._id === folderId ? updatedFolderData : f))
      );

      // Update in favorites list if present
      setFavoriteFolders((prevState) =>
        prevState.map((f) => (f._id === folderId ? updatedFolderData : f))
      );

      setIsEditModalOpen(false);
      setSelectedFolder(null);
      showToast("Folder updated successfully", "success");
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Failed to update folder";
      showToast(errorMsg, "error");
    }
  };

  return (
    <div className="w-full h-full px-4 md:px-6 lg:px-8 mt-6">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${toast.type === "error"
              ? "border border-red-400/70 bg-red-500/20 text-red-700"
              : "border border-green-400/70 bg-green-500/20 text-green-700"
            }`}
        >
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        {/* Title Row with Back Button and New Folder Button */}
        <div className="mb-3 flex items-center justify-between gap-4">
          {/* Back Button and Title */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/admin/folders")}
              className="flex items-center justify-center text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-500 transition-colors duration-200 flex-shrink-0"
              title="Back to Folders"
            >
              <IoArrowBack className="text-2xl" />
            </button>
            <h1 className="text-3xl font-bold text-navy-700 dark:text-white">
              {typeLabel} Folders
            </h1>
          </div>

          {/* New Folder Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 rounded-lg bg-gradient-to-br from-brand-500 to-brand-600 px-4 py-2 sm:px-6 sm:py-3 text-sm sm:text-base text-white font-semibold hover:shadow-lg hover:from-brand-600 hover:to-brand-700 transition-all duration-200 whitespace-nowrap flex-shrink-0"
          >
            <IoAdd className="text-lg sm:text-xl" />
            New Folder
          </button>
        </div>

        {/* Description Text */}
        <p className="text-gray-500 dark:text-gray-400 pl-11">
          Organize and manage your {typeLabel} folders
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-900/20">
          <p className="text-sm font-semibold text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      {/* Favorites Section */}
      {favoriteFolders.length > 0 && (
        <div className="mb-12">
          <div className="mb-6">
            <h2 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
              ⭐ Favorites
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
                ({favoriteFolders.length})
              </span>
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Your favorite {typeLabel} folders</p>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {favoriteFolders.map((folder) => (
              <PremiumFolderCard
                key={folder._id}
                folder={folder}
                onFavoriteToggle={handleFavoriteToggle}
                onDelete={handleDelete}
                onEdit={handleEdit}
                type={folderType}
              />
            ))}
          </div>
        </div>
      )}

      {/* All Folders Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
            📁 All Folders
            <span className="ml-2 text-sm font-normal text-gray-500 dark:text-gray-400">
              ({total})
            </span>
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">Browse all your {typeLabel} folders</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500 dark:border-gray-700 dark:border-t-brand-400" />
          </div>
        ) : allFolders.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 mb-8">
              {allFolders.map((folder) => (
                <PremiumFolderCard
                  key={folder._id}
                  folder={folder}
                  onFavoriteToggle={handleFavoriteToggle}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                  type={folderType}
                />
              ))}
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="flex items-center justify-center gap-6 py-6">
                {/* Rows Per Page Selector */}
                <div className="flex items-center gap-3">
                  {/* <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Items:</span> */}
                  <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-2xl p-1">
                    {[10, 20, 50].map((size) => (
                      <button
                        key={size}
                        onClick={() => {
                          setLimit(size);
                          setPage(1);
                        }}
                        className={`px-3 py-1 rounded-2xl text-sm font-medium transition-colors ${
                          limit === size
                            ? "bg-white text-navy-700 dark:bg-navy-700 dark:text-white shadow-sm"
                            : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Items per page.</span>
                </div>

                {/* Navigation */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="p-2 rounded-3xl bg-gray-300 border-2 border-gray-500 text-gray-800 hover:bg-gray-400 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <div className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-fit">
                    {page} of {totalPages}
                  </div>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                    className="p-2 rounded-3xl bg-gray-300 border-2 border-gray-500 text-gray-800 hover:bg-gray-400 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:border-gray-500 dark:text-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-200 py-12 dark:border-gray-700">
            <p className="text-gray-500 dark:text-gray-400 mb-4">No folders found</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-brand-500 hover:text-brand-600 font-semibold transition-colors"
            >
              Create your first folder →
            </button>
          </div>
        )}
      </div>

      {/* Create Folder Modal */}
      <CreateFolderModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        folderType={folderType}
        onSubmit={() => {
          setIsModalOpen(false);
          fetchFolders();
          showToast("Folder created successfully", "success");
        }}
      />

      {/* Edit Folder Modal */}
      {selectedFolder && (
        <EditFolderModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedFolder(null);
          }}
          onSubmit={handleEditSubmit}
          folderId={selectedFolder._id}
          folderName={selectedFolder.name}
          folderGradient={selectedFolder.gradient}
          folderType={selectedFolder.type}
        />
      )}
    </div>
  );
};

export default FoldersContent;
